import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import axiosInstance from "../../axios";
import { notification } from "antd";
import { AxiosError } from "axios";
import {
  ResetApiState,
  ResetPaginationState,
  PaginationType,
} from "../../redux.type";
import {
  AccessControlState,
  Role,
  RoleStatus,
  IRolePermission,
} from "./Access-control.util";

// Initial state
const initialState: AccessControlState = {
  roles: ResetApiState([]),
  role: ResetApiState(null),
  pagedRoles: ResetApiState(ResetPaginationState()),

  createRole: ResetApiState(null),
  editRole: ResetApiState(null),
  editRoleStatus: ResetApiState(null),
  deleteRole: ResetApiState(null),

  permissions: ResetApiState([]),
  permission: ResetApiState(null),

  createPermission: ResetApiState(null),

  invalidateFetchAll: 0,
  invalidateFetchPaged: 0,
  invalidateFetchAllPermissions: 0,
};

/**
 * ACTIONS
 */
const extraActions = createExtraActions();

function createExtraActions() {
  // 1. GET All Roles
  const fetchAllRoles = createAsyncThunk(
    "access-control/role/fetchAll",
    async (_, thunkApi) =>
      axiosInstance
        .get<Role[]>("/access-control/role")
        .then((response) => response.data)
        .catch((error: AxiosError) => thunkApi.rejectWithValue(error))
  );

  // 2. GET Paged Roles
  const fetchPagedRoles = createAsyncThunk(
    "access-control/role/fetchPaged",
    async (params: any, thunkApi) => {
      try {
        const response = await axiosInstance.get("/access-control/role/paged", {
          params,
        });

        const raw = response.data;
        const data = Array.isArray(raw.data) ? raw.data : [];
        const meta = raw.meta || {};

        // ✅ Normalize API shape → PaginationType<Role[]>
        const normalized: PaginationType<Role[]> = {
          page: Number(meta.page) || Number(params.page) || 1,
          limit: Number(meta.limit) || Number(params.limit) || data.length,
          count: Number(meta.total) || data.length,
          rows: data,
        };

        return normalized;
      } catch (error) {
        const axiosError = error as AxiosError;
        return thunkApi.rejectWithValue(
          axiosError.response?.data || axiosError.message
        );
      }
    }
  );

  // 3. GET One Role
  const fetchOneRole = createAsyncThunk(
    "access-control/role/fetchOne",
    async (id: string, thunkApi) =>
      axiosInstance
        .get<Role>(`/access-control/role/${id}`)
        .then((response) => response.data)
        .catch((error: AxiosError) => thunkApi.rejectWithValue(error))
  );

  // 4. CREATE Role
  const createRole = createAsyncThunk(
    "access-control/role/create",
    async (role: Omit<Role, "id">, thunkApi) =>
      axiosInstance
        .post<Role>("/access-control/role", role)
        .then((response) => response.data)
        .catch((error: AxiosError) =>
          thunkApi.rejectWithValue(error.response?.data)
        )
  );

  // 5. UPDATE Role
  const editRole = createAsyncThunk(
    "access-control/role/update",
    async (role: Role, thunkApi) =>
      axiosInstance
        .put<Role>(`/access-control/role/${role.id}`, role)
        .then((response) => response.data)
        .catch((error: AxiosError) =>
          thunkApi.rejectWithValue(error.response?.data)
        )
  );

  // 6. UPDATE Role Status
  const editRoleStatus = createAsyncThunk(
    "access-control/role/update-status",
    async ({ id, status }: { id: string; status: RoleStatus }, thunkApi) =>
      axiosInstance
        .patch<Role>(`/access-control/role/${id}/status`, { status })
        .then((response) => response.data)
        .catch((error: AxiosError) =>
          thunkApi.rejectWithValue(error.response?.data)
        )
  );

  // 7. DELETE Role
  const deleteRole = createAsyncThunk(
    "access-control/role/delete",
    async (id: string, thunkApi) =>
      axiosInstance
        .delete<Role>(`/access-control/role/${id}`)
        .then((response) => response.data)
        .catch((error: AxiosError) =>
          thunkApi.rejectWithValue(error.response?.data)
        )
  );

  // 9. UPDATE Role Permissions
  const createRolePermissions = createAsyncThunk(
    "access-control/role-permission/create",
    async ({ id, body }: { id: string | number; body: any[] }, thunkApi) => {
      try {
        const response = await axiosInstance.post<IRolePermission[]>(
          `/access-control/role-permission/${id}`,
          body
        );
        return response.data;
      } catch (error) {
        const axiosError = error as AxiosError;
        return thunkApi.rejectWithValue(
          axiosError.response?.data || axiosError.message
        );
      }
    }
  );

  return {
    fetchAllRoles,
    fetchPagedRoles,
    fetchOneRole,
    createRole,
    editRole,
    editRoleStatus,
    deleteRole,
    createRolePermissions,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<AccessControlState>
) {
  return {
    ...fetchAllRolesReducer(),
    ...fetchPagedRolesReducer(),
    ...fetchOneRoleReducer(),

    ...createRoleReducer(),
    ...editRoleReducer(),
    ...editRoleStatusReducer(),
    ...deleteRoleReducer(),

    ...createPermissionsReducer(),
  };

  function fetchAllRolesReducer() {
    return {
      ...builder.addCase(extraActions.fetchAllRoles.pending, (state) => {
        state.roles = {
          ...state.roles,
          loading: true,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.fetchAllRoles.fulfilled,
        (state, action) => {
          state.roles = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.fetchAllRoles.rejected,
        (state, action) => {
          state.roles = {
            loading: false,
            payload: [],
            successful: false,
            error: action.error.message || "Failed to fetch roles",
          };
        }
      ),
    };
  }

  function fetchPagedRolesReducer() {
    return {
      ...builder.addCase(extraActions.fetchPagedRoles.pending, (state) => {
        state.pagedRoles = {
          ...state.pagedRoles,
          loading: true,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.fetchPagedRoles.fulfilled,
        (state, action) => {
          state.pagedRoles = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.fetchPagedRoles.rejected,
        (state, action) => {
          state.pagedRoles = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.error.message || "Failed to fetch roles",
          };
        }
      ),
    };
  }

  function fetchOneRoleReducer() {
    return {
      ...builder.addCase(extraActions.fetchOneRole.pending, (state) => {
        state.role = {
          ...state.role,
          loading: true,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.fetchOneRole.fulfilled,
        (state, action) => {
          state.role = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.fetchOneRole.rejected,
        (state, action) => {
          state.role = {
            loading: false,
            payload: null,
            successful: false,
            error: action.error.message || "Failed to fetch role",
          };
        }
      ),
    };
  }

  function createRoleReducer() {
    return {
      ...builder.addCase(extraActions.createRole.pending, (state) => {
        state.createRole = {
          payload: null,
          loading: true,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.createRole.fulfilled,
        (state, action: PayloadAction<Role>) => {
          notification.success({
            message: "Role Created Successfully",
          });

          state.createRole = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };

          // Invalidate for refetch
          state.invalidateFetchAll += 1;
          state.invalidateFetchPaged += 1;
        }
      ),
      ...builder.addCase(extraActions.createRole.rejected, (state, action) => {
        notification.error({
          message: "Failed to Create Role",
          description: action.error.message || "Failed to create role",
        });

        state.createRole = {
          loading: false,
          payload: null,
          successful: false,
          error: action.error.message || "Failed to create role",
        };
      }),
    };
  }

  function editRoleReducer() {
    return {
      ...builder.addCase(extraActions.editRole.pending, (state) => {
        state.editRole = {
          payload: null,
          loading: true,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.editRole.fulfilled,
        (state, action: PayloadAction<Role>) => {
          notification.success({
            message: "Role Edited Successfully",
          });

          state.editRole = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };

          // Invalidate for refetch
          state.invalidateFetchAll += 1;
          state.invalidateFetchPaged += 1;
        }
      ),
      ...builder.addCase(extraActions.editRole.rejected, (state, action) => {
        notification.error({
          message: "Failed to Edit Role",
          description: action?.error?.message,
        });

        state.editRole = {
          loading: false,
          payload: null,
          successful: false,
          error: action.error.message || "Failed to edit role",
        };
      }),
    };
  }

  function editRoleStatusReducer() {
    return {
      ...builder.addCase(extraActions.editRoleStatus.pending, (state) => {
        state.editRoleStatus = {
          payload: null,
          loading: true,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.editRoleStatus.fulfilled,
        (state, action: PayloadAction<Role>) => {
          notification.success({ message: "Status Updated Successfully" });

          state.editRoleStatus = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };

          if (state.roles.payload.length) {
            const index = state.roles.payload.findIndex(
              (s) => s.id === action.payload.id
            );
            if (index !== -1) {
              state.roles.payload[index] = action.payload;
            }
          }

          const index = state.pagedRoles.payload.rows.findIndex(
            (s) => s.id === action.payload.id
          );
          if (index !== -1) {
            state.pagedRoles.payload.rows[index] = action.payload;
          }
        }
      ),
      ...builder.addCase(
        extraActions.editRoleStatus.rejected,
        (state, action) => {
          notification.error({
            message: "Failed to Change Status of Role",
            description: action?.error?.message,
          });

          state.editRoleStatus = {
            loading: false,
            payload: null,
            successful: false,
            error: action.error.message || "Failed to Change Status of Role",
          };
        }
      ),
    };
  }

  function deleteRoleReducer() {
    return {
      ...builder.addCase(extraActions.deleteRole.pending, (state) => {
        state.deleteRole = {
          payload: null,
          loading: true,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.deleteRole.fulfilled,
        (state, action: PayloadAction<Role>) => {
          notification.success({
            message: "Role Deleted Successfully",
          });

          state.deleteRole = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };

          // Remove from roles
          if (state.roles.payload.length) {
            const index = state.roles.payload.findIndex(
              (s) => s.id === action.payload.id
            );
            if (index !== -1) {
              state.roles.payload.splice(index, 1);
            }
          }

          // Remove from pagedRoles
          if (
            state.pagedRoles.payload.rows &&
            state.pagedRoles.payload.rows.length > 0
          ) {
            state.pagedRoles.payload.rows =
              state.pagedRoles.payload.rows.filter(
                (s) => s.id !== action.payload.id
              );

            state.pagedRoles.payload.count -= 1;
          }
        }
      ),
      ...builder.addCase(extraActions.deleteRole.rejected, (state, action) => {
        notification.error({
          message: "Failed to Delete Role",
          description: action?.error?.message,
        });

        state.deleteRole = {
          loading: false,
          payload: null,
          successful: false,
          error: action.error.message || "Failed to delete role",
        };
      }),
    };
  }

  function createPermissionsReducer() {
    return {
      ...builder.addCase(
        extraActions.createRolePermissions.pending,
        (state) => {
          state.createPermission = {
            payload: null,
            loading: true,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createRolePermissions.fulfilled,
        (state, action: PayloadAction<IRolePermission[]>) => {
          notification.success({
            message: "Permissions Updated Successfully",
          });

          state.createPermission = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };

          // Invalidate for refetch
          state.invalidateFetchAllPermissions += 1;
        }
      ),
      ...builder.addCase(
        extraActions.createRolePermissions.rejected,
        (state, action) => {
          notification.error({
            message: "Failed to Update Permissions",
            description: action.error.message || "Failed to update permissions",
          });

          state.createPermission = {
            loading: false,
            payload: null,
            successful: false,
            error: action.error.message || "Failed to update permissions",
          };
        }
      ),
    };
  }
}

const AccessControlSlice = createSlice({
  name: "access-control",
  initialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const AccessControlActions = {
  ...AccessControlSlice.actions,
  ...extraActions,
};
export const AccessControlReducer = AccessControlSlice.reducer;
